import React, { memo, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { SharedValue, interpolateColor, useAnimatedStyle } from 'react-native-reanimated';
import Svg, { Circle, Defs, G, Line, LinearGradient, Path, RadialGradient, Stop } from 'react-native-svg';

import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { textVariants } from '@/components/lib/typography';
import { designSystem, withOpacity } from '@/styles/styles';

import { degToRad, describeArc } from '../../utils/mystery-wheel-utils';
import { DEFAULT_WHEEL_SIZE } from './MysteryWheel';

export const BORDER_WIDTH = 8;
export const WHEEL_OFFSET_DEG = 90; // offset to rotate the wheel so the first slice is at the top
const SLICE_BORDER_OVERLAP_OFFSET = 0.1; // offset to avoid overlapping the slice borders
export const CENTER_LOGO_SIZE = 116; // size of the logo displayed in the middle of the wheel

type SliceData = {
    path: string;
    textAngle: number;
    textX: number;
    textY: number;
    isEven: boolean;
    label: number;
    index: number;
    startAngle: number;
};

const SliceLabel = ({
    slice,
    rotation,
    wheelSize,
}: {
    slice: SliceData;
    rotation: SharedValue<number>;
    wheelSize: number;
}) => {
    const animatedStyle = useAnimatedStyle(() => {
        const normalizedRotation = ((((-rotation.value % 360) + 360) % 360) / 360) * 16;
        const sliceProgress = slice.index - normalizedRotation;

        const color = interpolateColor(
            sliceProgress,
            [-0.75, 0, 0.75],
            [designSystem.colors.neutral400, ds.colorValue.text.primary, designSystem.colors.neutral400]
        );

        return { color };
    });

    return (
        <Box
            width={(wheelSize - CENTER_LOGO_SIZE) / 2}
            height={textVariants.headlineSmall.lineHeight}
            alignItems={'center'}
            position={'absolute'}
            left={slice.textX}
            top={slice.textY}
            style={{ transform: [{ rotate: `${slice.textAngle}deg` }], ...styles.textContainer }}
        >
            <Animated.Text
                key={`text-${slice.index}`}
                style={[
                    styles.text,
                    {
                        top: -(textVariants.headlineSmall.lineHeight / 2),
                    },
                    animatedStyle,
                ]}
            >
                {`${slice.label}x`}
            </Animated.Text>
        </Box>
    );
};

export const SvgWheel = memo(
    ({
        size = DEFAULT_WHEEL_SIZE,
        numbers = [],
        rotation,
    }: {
        size?: number;
        numbers: number[];
        rotation: SharedValue<number>;
    }) => {
        const wheelDiameter = size - BORDER_WIDTH;
        const center = size / 2;
        const wheelRadius = wheelDiameter / 2;
        const sliceAngle = 360 / numbers.length;

        const slicesData = useMemo(() => {
            return numbers.map((label, index) => {
                const startAngle = index * sliceAngle - WHEEL_OFFSET_DEG; // -90 to rotate the wheel so the first slice is at the top
                const path = describeArc(center, center, wheelRadius, startAngle, startAngle + sliceAngle);
                const textAngle = startAngle; // center of the slice
                const textRadius = CENTER_LOGO_SIZE / 2; // to center the text, add 50 which is half of the logo displayed in the middle
                const textX = center + textRadius * Math.cos(degToRad(textAngle));
                const textY = center + textRadius * Math.sin(degToRad(textAngle));
                const isEven = index % 2 === 0;
                return {
                    path,
                    textAngle,
                    textX,
                    textY,
                    isEven,
                    label,
                    index,
                    startAngle,
                };
            });
        }, [center, numbers, sliceAngle, wheelRadius]);

        return (
            <Box width={size} height={size}>
                <Svg width={size} height={size}>
                    <Defs>
                        {/* using a radial gradient to mimic the inner shadow around the border of the wheel*/}
                        <RadialGradient id={'innerShadow'}>
                            <Stop offset={'97%'} stopColor={designSystem.colors.neutralBlack} stopOpacity={'0'} />
                            <Stop offset={'100%'} stopColor={designSystem.colors.neutralBlack} stopOpacity={'0.5'} />
                        </RadialGradient>
                        {slicesData.map((slice, index) => {
                            return (
                                <LinearGradient
                                    key={`oddSliceGradient${index}`}
                                    id={`oddSliceGradient${index}`}
                                    gradientUnits={'userSpaceOnUse'}
                                    gradientTransform={`rotate(${slice.startAngle}, ${center}, ${center})`}
                                    x1={center}
                                    y1={center}
                                    x2={center + wheelRadius}
                                    y2={center}
                                >
                                    <Stop offset="100%" stopColor={designSystem.colors.neutralBlack} stopOpacity="0" />
                                    <Stop offset="0%" stopColor={designSystem.colors.neutralBlack} stopOpacity="0.3" />
                                </LinearGradient>
                            );
                        })}
                    </Defs>
                    {/** rotating the wheel by half a slice angle so it aligns on the middle of the first slice */}
                    <G origin={`${center}, ${center}`} rotation={-sliceAngle / 2}>
                        {slicesData.map((slice, index) => (
                            <React.Fragment key={`fill-${slice.index}`}>
                                <Path d={slice.path} fill={designSystem.colors.neutral800} />
                                {!slice.isEven && <Path d={slice.path} fill={`url(#oddSliceGradient${index})`} />}
                            </React.Fragment>
                        ))}
                        {/* rendering the divider lines of each slice separately so that they don't overlap */}
                        {slicesData.map(slice => {
                            const startAngle = slice.startAngle - SLICE_BORDER_OVERLAP_OFFSET;
                            const endAngle = slice.startAngle + sliceAngle + SLICE_BORDER_OVERLAP_OFFSET;
                            const startLineEndX = center + wheelRadius * Math.cos(degToRad(startAngle));
                            const startLineEndY = center + wheelRadius * Math.sin(degToRad(startAngle));
                            const endLineEndX = center + wheelRadius * Math.cos(degToRad(endAngle));
                            const endLineEndY = center + wheelRadius * Math.sin(degToRad(endAngle));
                            const strokeColor = slice.isEven
                                ? withOpacity(designSystem.colors.neutralBlack, 0.2)
                                : withOpacity(ds.colorValue.icon.primary, 0.02);
                            return (
                                <React.Fragment key={`divider-${slice.index}`}>
                                    <Line
                                        x1={center}
                                        y1={center}
                                        x2={startLineEndX}
                                        y2={startLineEndY}
                                        stroke={strokeColor}
                                        strokeWidth={1}
                                    />
                                    <Line
                                        x1={center}
                                        y1={center}
                                        x2={endLineEndX}
                                        y2={endLineEndY}
                                        stroke={strokeColor}
                                        strokeWidth={1}
                                    />
                                </React.Fragment>
                            );
                        })}
                    </G>
                    {/* inner shadow around the border of the wheel */}
                    <Circle cx={center} cy={center} r={wheelRadius} fill={'url(#innerShadow)'} />

                    {/* the border around the wheel */}
                    <Circle
                        cx={center}
                        cy={center}
                        r={wheelRadius}
                        fill={'none'}
                        stroke={designSystem.colors.neutral600}
                        strokeWidth={BORDER_WIDTH}
                    />

                    {/* the stroke of the border of the wheel */}
                    <Circle
                        cx={center}
                        cy={center}
                        r={wheelRadius + 3}
                        fill={'none'}
                        stroke={withOpacity(ds.colorValue.icon.primary, 0.02)}
                        strokeWidth={1}
                    />
                </Svg>
                <Box position={'absolute'} width={size} height={size}>
                    {slicesData.map(slice => {
                        return (
                            <SliceLabel
                                key={`label-${slice.index}`}
                                wheelSize={size}
                                slice={slice}
                                rotation={rotation}
                            />
                        );
                    })}
                </Box>
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    textContainer: { transformOrigin: 'left top' },
    text: { position: 'absolute', ...textVariants.headlineSmall, fontWeight: '700' },
});
